// Master Document Editor shell (Jul 2026, Randy). A thin split-screen host: it
// owns the overlay, sizing, and the "Open Side by Side" state, then renders one
// or TWO reusable DocEditorPane surfaces. On desktop/wide viewports the second
// pane is available freely; on a phone it's landscape-gated (portrait shows a
// CTA to turn the device). The heavy editor logic lives in DocEditorPane.
import { useEffect, useRef, useState } from "react";
import { X, MagnifyingGlass, DeviceMobile, FileText, Swap, GitDiff } from "@phosphor-icons/react";
import DocEditorPane from "@/components/DocEditorPane";

const TIER_LABEL = { 1: "Tier 1 · Soul", 2: "Tier 2 · Working memory", 3: "Tier 3 · Deep archive" };

// Minimum viewport width to fit two panes. Below this the split is landscape-gated.
const MIN_SPLIT_WIDTH = 768;

// Reactively answers "is there room for two panes?" A phone in portrait can't
// fit them (→ landscape CTA); a phone rotated to landscape or any desktop can.
function useSplitCapability() {
  const compute = () => {
    if (typeof window === "undefined" || !window.matchMedia) return { wideEnough: true, isTouch: false };
    return {
      wideEnough: window.matchMedia(`(min-width: ${MIN_SPLIT_WIDTH}px)`).matches,
      isTouch: window.matchMedia("(pointer: coarse)").matches,
    };
  };
  const [cap, setCap] = useState(compute);
  useEffect(() => {
    const onChange = () => setCap(compute());
    window.addEventListener("resize", onChange);
    window.addEventListener("orientationchange", onChange);
    return () => {
      window.removeEventListener("resize", onChange);
      window.removeEventListener("orientationchange", onChange);
    };
  }, []);
  return cap;
}

// The right half before a second file is chosen: a searchable, tier-grouped
// picker of every file in the silo.
function RightPanePicker({ docs = [], currentDocId, onPick, onClose }) {
  const [q, setQ] = useState("");
  const needle = q.trim().toLowerCase();
  const filtered = needle ? docs.filter((d) => (d.title || "").toLowerCase().includes(needle)) : docs;
  const byTier = { 1: [], 2: [], 3: [] };
  for (const d of filtered) byTier[d.tier || 3].push(d);

  return (
    <div className="flex flex-col h-full min-w-0 flex-1 bg-[#FDFCF8]" data-testid="doc-editor-picker">
      <div className="flex items-center gap-3 px-4 sm:px-5 py-3 border-b hairline shrink-0 bg-white">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-[#1C1B1A]">Open a second file</div>
          <div className="text-[11px] text-[#6B6A68] mt-0.5">Edit two documents at once</div>
        </div>
        <button onClick={onClose} data-testid="doc-editor-picker-close" className="p-2 rounded-md text-[#6B6A68] hover:bg-[#F0F1EE]"><X size={18} /></button>
      </div>

      <div className="px-4 sm:px-5 py-2.5 border-b hairline shrink-0 bg-white">
        <div className="relative">
          <MagnifyingGlass size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#6B6A68]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search your files…"
            data-testid="doc-editor-picker-search"
            className="w-full bg-white border hairline rounded-md pl-8 pr-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]"
          />
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-5 space-y-4">
        {filtered.length === 0 ? (
          <div className="h-full flex items-center justify-center text-sm text-[#6B6A68]" data-testid="doc-editor-picker-empty">
            No files match.
          </div>
        ) : (
          [1, 2, 3].map((t) =>
            byTier[t].length ? (
              <div key={t}>
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#9A9890] mb-1.5">{TIER_LABEL[t]}</div>
                <div className="space-y-1">
                  {byTier[t].map((d) => {
                    const isSelf = d.id === currentDocId;
                    return (
                      <button
                        key={d.id}
                        disabled={isSelf}
                        onClick={() => !isSelf && onPick(d)}
                        data-testid={`doc-editor-picker-item-${d.id}`}
                        className={`w-full text-left flex items-center gap-2.5 px-3 py-2 rounded-md border hairline bg-white transition-colors ${isSelf ? "opacity-50 cursor-not-allowed" : "hover:border-[#4A5D4E] hover:bg-[#EAF3EC]"}`}
                      >
                        <FileText size={15} weight="duotone" className="text-[#4A5D4E] shrink-0" />
                        <span className="text-sm text-[#1C1B1A] truncate flex-1">{d.title || "Untitled"}</span>
                        {isSelf && <span className="text-[10px] uppercase tracking-[0.12em] text-[#6B6A68] shrink-0">On the left</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : null
          )
        )}
      </div>
    </div>
  );
}

// Cheap line-level LCS diff for the Compare view. Capped so a giant transcript
// can't blow up memory; above the cap we fall back to a summary.
const DIFF_MAX_LINES = 2000;
function lineDiff(a, b) {
  const A = (a || "").split("\n");
  const B = (b || "").split("\n");
  if (A.length > DIFF_MAX_LINES || B.length > DIFF_MAX_LINES) return null;
  const n = A.length, m = B.length;
  const dp = Array.from({ length: n + 1 }, () => new Uint32Array(m + 1));
  for (let i = n - 1; i >= 0; i--)
    for (let j = m - 1; j >= 0; j--)
      dp[i][j] = A[i] === B[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
  const out = [];
  let i = 0, j = 0;
  while (i < n && j < m) {
    if (A[i] === B[j]) { out.push({ t: "eq", text: A[i] }); i++; j++; }
    else if (dp[i + 1][j] >= dp[i][j + 1]) { out.push({ t: "del", text: A[i] }); i++; }
    else { out.push({ t: "add", text: B[j] }); j++; }
  }
  while (i < n) out.push({ t: "del", text: A[i++] });
  while (j < m) out.push({ t: "add", text: B[j++] });
  return out;
}

// Full-screen diff of the two open files' LIVE text (left = base, right = compared).
function DiffOverlay({ leftDoc, rightDoc, leftText, rightText, onClose }) {
  const rows = lineDiff(leftText, rightText);
  const added = rows ? rows.filter((r) => r.t === "add").length : 0;
  const removed = rows ? rows.filter((r) => r.t === "del").length : 0;
  const identical = (leftText || "") === (rightText || "");
  return (
    <div className="absolute inset-0 z-40 flex flex-col bg-white" data-testid="doc-editor-diff">
      <div className="flex items-center gap-3 px-4 sm:px-5 py-3 border-b hairline shrink-0 bg-[#FDFCF8]">
        <GitDiff size={18} weight="duotone" className="text-[#4A5D4E] shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-[#1C1B1A] truncate">
            Compare · <span className="text-[#2C6E92]">{leftDoc?.title || "Left"}</span> ↔ <span className="text-[#B06A2C]">{rightDoc?.title || "Right"}</span>
          </div>
          <div className="text-[11px] text-[#6B6A68] mt-0.5" data-testid="doc-editor-diff-summary">
            {identical ? "The two files are identical." : (
              <>
                <span className="text-[#2F8F4E]">+{added} added</span> · <span className="text-[#C16E5A]">−{removed} removed</span>
                <span className="text-[#9A9890]"> (relative to the left file)</span>
              </>
            )}
          </div>
        </div>
        <button onClick={onClose} data-testid="doc-editor-diff-close" className="p-2 rounded-md text-[#6B6A68] hover:bg-[#F0F1EE]"><X size={18} /></button>
      </div>
      <div className="flex-1 min-h-0 overflow-auto p-4 sm:p-5 font-mono text-xs leading-relaxed">
        {rows === null ? (
          <div className="h-full flex items-center justify-center text-center text-sm text-[#6B6A68] px-6" data-testid="doc-editor-diff-toobig">
            One of these files is too large for a line-by-line view.<br />
            They are {identical ? "identical" : "different"} · left {(leftText || "").length.toLocaleString()} chars · right {(rightText || "").length.toLocaleString()} chars.
          </div>
        ) : identical ? (
          <div className="h-full flex items-center justify-center text-sm text-[#6B6A68]">No differences — the files match line for line.</div>
        ) : (
          <div className="space-y-0">
            {rows.map((r, k) => (
              <div key={k}
                className={`whitespace-pre-wrap break-words px-2 -mx-2 rounded-sm ${
                  r.t === "add" ? "bg-[#E9F3EC] text-[#1C1B1A]" : r.t === "del" ? "bg-[#FBEBE6] text-[#8A4A3A] line-through decoration-[#C16E5A]/40" : "text-[#6B6A68]"
                }`}>
                <span className={`select-none mr-2 ${r.t === "add" ? "text-[#2F8F4E]" : r.t === "del" ? "text-[#C16E5A]" : "text-[#C7C5BE]"}`}>
                  {r.t === "add" ? "+" : r.t === "del" ? "−" : " "}
                </span>
                {r.text || "\u00A0"}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function DocEditorModal({ open, onClose, siloId, doc, silos = [], docs = [], onSaved, onSwitchTab }) {
  const [secondDoc, setSecondDoc] = useState(null);
  const [leftDocState, setLeftDocState] = useState(null); // set only after a Swap
  const [pickerOpen, setPickerOpen] = useState(false);
  const [showLandscapeCta, setShowLandscapeCta] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);
  const [leftDirty, setLeftDirty] = useState(false);
  const [rightDirty, setRightDirty] = useState(false);
  const { wideEnough, isTouch } = useSplitCapability();
  const leftTextRef = useRef("");
  const rightTextRef = useRef("");

  // Reset the split whenever the modal closes or the primary file changes.
  useEffect(() => {
    if (!open) {
      setSecondDoc(null); setLeftDocState(null); setPickerOpen(false); setShowLandscapeCta(false);
      setCompareOpen(false); setLeftDirty(false); setRightDirty(false);
    }
  }, [open]);
  useEffect(() => {
    setSecondDoc(null); setLeftDocState(null); setPickerOpen(false); setCompareOpen(false); setRightDirty(false);
  }, [doc?.id]);

  const leftDoc = leftDocState || doc;
  const splitActive = pickerOpen || !!secondDoc;

  const attemptCloseAll = () => {
    if ((leftDirty || rightDirty) && !window.confirm("You have unsaved edits. Close without saving?")) return;
    onClose?.();
  };

  // Escape closes the whole modal (guarded); the panes guard their own buttons.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") { compareOpen ? setCompareOpen(false) : attemptCloseAll(); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, leftDirty, rightDirty, compareOpen]);

  if (!open || !doc) return null;

  const onOpenSideBySide = () => {
    // Authoritative at click-time (avoids any stale matchMedia/listener race).
    const roomy = typeof window === "undefined" || window.innerWidth >= MIN_SPLIT_WIDTH;
    if (!roomy) { setShowLandscapeCta(true); return; }
    setShowLandscapeCta(false);
    setPickerOpen(true);
  };
  const closeRight = () => { setSecondDoc(null); setLeftDocState(null); setPickerOpen(false); setCompareOpen(false); setRightDirty(false); };

  const swapPanes = () => {
    if (!secondDoc) return;
    if ((leftDirty || rightDirty) && !window.confirm("Swapping reloads both files and discards unsaved edits. Continue?")) return;
    const curLeft = leftDoc, curRight = secondDoc;
    setLeftDocState(curRight);
    setSecondDoc(curLeft);
  };
  const openCompare = () => { if (secondDoc) setCompareOpen(true); };

  const sideBySideState = splitActive ? "hidden" : wideEnough ? "enabled" : "ghost";

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-2 sm:p-4" data-testid="doc-editor-modal"
      style={{
        // Feb 2026 pt89g (Randy) — iOS Safari safe-area padding so the top
        // header (close + filename) and bottom footer (Revert / Send to
        // staging / Save) never get clipped by Safari's URL bar or home
        // indicator. dvh (dynamic viewport height) also resolves Safari's
        // notorious vh-includes-URL-bar bug.
        paddingTop: "max(0.5rem, env(safe-area-inset-top))",
        paddingBottom: "max(0.5rem, env(safe-area-inset-bottom))",
      }}>
      <div className="absolute inset-0 bg-[#1C1B1A]/50 backdrop-blur-sm" onClick={attemptCloseAll} />

      <div className={`relative z-10 w-full h-[92dvh] max-h-[92dvh] bg-white border hairline rounded-xl shadow-2xl overflow-hidden flex ${splitActive ? "max-w-7xl" : "max-w-5xl"}`}
        data-testid={splitActive ? "doc-editor-split" : "doc-editor-single"}>

        {/* Landscape / widen CTA — in-DOM (mobile toasts are hidden on this surface) */}
        {showLandscapeCta && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2 px-3 py-2 rounded-full bg-[#1C1B1A] text-white text-xs shadow-lg max-w-[92%]"
            data-testid="doc-editor-landscape-cta">
            <DeviceMobile size={15} weight="duotone" className="shrink-0" />
            <span className="truncate">
              {isTouch ? "Turn your phone to landscape to open two files side-by-side." : "Widen your browser window to open two files side-by-side."}
            </span>
            <button onClick={() => setShowLandscapeCta(false)} data-testid="doc-editor-landscape-cta-dismiss"
              className="ml-1 shrink-0 px-2 py-0.5 rounded-full bg-white/15 hover:bg-white/25">Got it</button>
          </div>
        )}

        {/* Left (primary) pane — its close button self-guards LEFT dirty; we add a
            RIGHT-dirty guard here so closing via the left X never silently drops
            unsaved edits in the second pane. */}
        <DocEditorPane
          key={leftDoc?.id}
          doc={leftDoc}
          siloId={siloId}
          silos={silos}
          onSaved={onSaved}
          onClose={() => {
            if (rightDirty && !window.confirm("The other file has unsaved edits. Close both without saving?")) return;
            onClose?.();
          }}
          onSwitchTab={onSwitchTab}
          onDirtyChange={setLeftDirty}
          textRef={leftTextRef}
          sideBySide={{ state: sideBySideState, onClick: onOpenSideBySide }}
        />

        {splitActive && (
          <div className="relative w-px bg-[#E0DED8] shrink-0" data-testid="doc-editor-divider">
            {/* Cross-pane controls live between the two files: Swap sides + Compare. */}
            {secondDoc && (
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20 flex flex-col gap-1.5">
                <button onClick={swapPanes} data-testid="doc-editor-swap" title="Swap sides"
                  className="w-8 h-8 rounded-full bg-white border hairline shadow-sm flex items-center justify-center text-[#4A5D4E] hover:bg-[#EAF3EC] hover:border-[#4A5D4E] transition-colors">
                  <Swap size={15} weight="duotone" />
                </button>
                <button onClick={openCompare} data-testid="doc-editor-compare" title="Compare the two files"
                  className="w-8 h-8 rounded-full bg-white border hairline shadow-sm flex items-center justify-center text-[#4A5D4E] hover:bg-[#EAF3EC] hover:border-[#4A5D4E] transition-colors">
                  <GitDiff size={15} weight="duotone" />
                </button>
              </div>
            )}
          </div>
        )}

        {splitActive && (
          secondDoc ? (
            <DocEditorPane
              key={secondDoc.id}
              doc={secondDoc}
              siloId={siloId}
              silos={silos}
              onSaved={onSaved}
              onClose={closeRight}
              onSwitchTab={onSwitchTab}
              onDirtyChange={setRightDirty}
              textRef={rightTextRef}
              testidSuffix="-2"
              sideBySide={{ state: "hidden" }}
            />
          ) : (
            <RightPanePicker
              docs={docs}
              currentDocId={leftDoc?.id}
              onPick={(d) => { setSecondDoc(d); setPickerOpen(false); }}
              onClose={closeRight}
            />
          )
        )}

        {compareOpen && secondDoc && (
          <DiffOverlay
            leftDoc={leftDoc}
            rightDoc={secondDoc}
            leftText={leftTextRef.current}
            rightText={rightTextRef.current}
            onClose={() => setCompareOpen(false)}
          />
        )}
      </div>
    </div>
  );
}
